import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Tabs } from 'antd';
import type { Moment } from 'moment';

import { areRequiredFieldsFilled } from './utils/promoStateUtils';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { buildAttributesPayload } from '../../utils/entityAttributes';
import { TRANSFORMS } from '../../utils/buildPromoPayload';
import type { ApiPPG, PPGRow } from '../../types/promoMetrics';
import type { RuleOption } from '../../config/tpoGenConfig';
import { buildOptionResolvers } from '../../rules/resolvers/optionResolvers';
import { useScopeFieldStates } from '../../rules/store/selectors';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import useGetPromoData from './hooks/queries/useGetPromoData';
import useRecommendedValues from './hooks/queries/useRecommendedValues';
import useProductAttributeFilters from './hooks/queries/useProductAttributeFilters';
import useCreatePromoMutation from './hooks/mutations/useCreatePromoMutation';
import useUpliftMutation from './hooks/mutations/useUpliftMutation';
import usePpgData from './hooks/ui/usePpgData';
import usePromoDirtyState from './hooks/ui/usePromoDirtyState';
import usePromoSave from './hooks/ui/usePromoSave';
import useUpliftTrigger from './hooks/ui/useUpliftTrigger';
import { cascadeTriggerFields } from './utils/applyFormCascade';
import PromoDesigner from './PromoDesigner';
import { useOptionalRuleStoreApi } from '../../rules/store/FieldRulesProvider';

type PromoTabProps = {
    planId: string;
    eventId: string;
    /** Per-promo-tab id (the EventsView tab key); isolates this tab's rule-engine state. */
    instanceId: string;
    promotionId?: string;
    onPromoSaved?: (promotionId: string) => void;
    onDirtyChange?: (isDirty: boolean) => void;
};

function formatDate(val: Moment | string | undefined): string {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val.format('YYYY-MM-DD');
}

const PromoTab: FC<PromoTabProps> = ({ planId, eventId, instanceId, promotionId, onPromoSaved, onDirtyChange }) => {
    const [promoForm] = Form.useForm();
    const { parsedPromoOptions } = useCustomerConfig();
    const { promoFields, formCascadeRules, entityAttributes } = useTpoGenConfig();
    const [savedPromotionId, setSavedPromotionId] = useState<string | null>(promotionId ?? null);
    const [selectedPpgIds, setSelectedPpgIds] = useState<number[]>([]);

    const noApiFieldNames = useMemo(
        () => promoFields.filter((f) => f.apiKey === null).map((f) => f.name),
        [promoFields],
    );
    // Offer fields (and their unit-toggle unit field) whose user edits should re-fetch uplift.
    const offerTriggerFieldNames = useMemo(
        () =>
            new Set(
                promoFields
                    .filter((f) => f.offerMapping)
                    .flatMap((f) => (f.unitToggle ? [f.name, f.unitToggle.unitField] : [f.name])),
            ),
        [promoFields],
    );
    // Form fields whose user edits should re-run the deterministic form -> metric cascade:
    // each rule's triggers + input/unit fields + the `promo:` sources of its `when`/variant
    // conditions (so a gating field change - e.g. status/tactic - also re-runs the cascade).
    const cascadeTriggerFieldNames = useMemo(() => cascadeTriggerFields(formCascadeRules), [formCascadeRules]);

    const { currentPlanDetails } = usePlanDetailsContext();
    const dim2AttributeName = currentPlanDetails?.dim2_attrs?.[0] ?? '';

    const effectivePromotionId = promotionId ?? savedPromotionId ?? null;
    const isEditMode = !!promotionId || !!savedPromotionId;

    // Rule-engine: mirror promo-level context into the store. `hasEntityId` is
    // per-promo-instance (each tab has its own); `planYear` is plan-global.
    const ruleStore = useOptionalRuleStoreApi();
    useEffect(() => {
        if (!ruleStore) return;
        ruleStore.getState().setPromoHasEntityId(instanceId, !!effectivePromotionId);
        ruleStore.getState().setContext({ planYear: currentPlanDetails?.plan_year });
    }, [ruleStore, instanceId, effectivePromotionId, currentPlanDetails?.plan_year]);

    // --- queries ---
    const {
        promoFormValues: existingPromoFormValues,
        ppgData: existingPpgData,
        ppgIds: existingPpgIds,
        promoMeta,
        isPending: isLoadingPromoData,
    } = useGetPromoData(effectivePromotionId);

    const { options: productOptions, isPending: isLoadingProducts } = useProductAttributeFilters({ eventId });

    const rawPromoFormValues = Form.useWatch([], promoForm);
    const promoFormValues: Record<string, unknown> = useMemo(() => rawPromoFormValues ?? {}, [rawPromoFormValues]);

    // Engine-derived per-field UI state for the promo scope (drives the required-fields gate).
    const baseOptionsByTarget = useMemo(() => {
        const map: Record<string, RuleOption[]> = {};
        for (const f of promoFields) {
            if (f.options) map[`promo:${f.name}`] = f.options as RuleOption[];
        }
        return map;
    }, [promoFields]);
    const promoResolvers = useMemo(
        () => buildOptionResolvers({ parsedPromoOptions, baseOptionsByTarget }),
        [parsedPromoOptions, baseOptionsByTarget],
    );
    const promoFieldStates = useScopeFieldStates('promo', promoResolvers, instanceId);

    const [hasFormErrors, setHasFormErrors] = useState(false);
    const validationFrameRef = useRef<number>();

    useEffect(() => {
        if (validationFrameRef.current !== undefined) {
            cancelAnimationFrame(validationFrameRef.current);
        }
        validationFrameRef.current = requestAnimationFrame(() => {
            promoForm
                .validateFields({ validateOnly: true })
                .then(() => setHasFormErrors(false))
                .catch(() => setHasFormErrors(true));
        });
    }, [promoForm, rawPromoFormValues]);

    const isAddProductsDisabled = !areRequiredFieldsFilled(promoFieldStates, promoFormValues);
    const promotionType = (promoFormValues['promoType'] as string) ?? '';
    const promoStartDate = formatDate(promoFormValues['promoStartDate'] as Moment | string | undefined);
    const promoEndDate = formatDate(promoFormValues['promoEndDate'] as Moment | string | undefined);
    const craStartDate = formatDate(promoFormValues['craStartDate'] as Moment | string | undefined);
    const craEndDate = formatDate(promoFormValues['craEndDate'] as Moment | string | undefined);

    // Dynamic, config-driven attributes object (promotion_type, tactic, and any
    // future promotion-level attribute) for the recommended-values request.
    const promoAttributes = useMemo(
        () => buildAttributesPayload(entityAttributes, promoFields, promoFormValues, 'promotion', TRANSFORMS),
        [entityAttributes, promoFields, promoFormValues],
    );

    const {
        ppgData: fetchedPpgData,
        isLoading: isLoadingRecommendedValues,
        isFetchingNextPage: isLoadingNextPage,
        hasNextPage: hasMoreRecommendedValues,
        fetchNextPage: fetchNextRecommendedPage,
    } = useRecommendedValues({
        planId,
        eventId,
        attributes: promoAttributes,
        startDate: promoStartDate,
        endDate: promoEndDate,
        craStartDate,
        craEndDate,
        ppgIds: selectedPpgIds,
        enabled: areRequiredFieldsFilled(promoFieldStates, promoFormValues) && selectedPpgIds.length > 0,
    });

    // --- ui hooks ---
    const {
        ppgData,
        hasProducts,
        baseline,
        onMetricChange,
        applyFormCascade,
        onAddProducts,
        resetEditableSpendsToZero,
        resetPpgToExisting,
        clearPpgState,
        snapshotBaseline,
    } = usePpgData({ existingPpgData, existingPpgIds, fetchedPpgData, formCascadeRules, promoFormValues });

    // --- uplift ---
    // Mutation for POST /model_uplift (network layer only)
    const { mutateAsync: upliftMutateAsync, isPending: isUpliftPending } = useUpliftMutation();

    // Orchestrates validation, payload building, and response cascade for uplift
    const { triggerUplift, triggerUpliftBeforeSave, triggerUpliftAuto, triggerUpliftDebounced, isUpliftApplicable } =
        useUpliftTrigger({
            ppgData,
            promoFormValues,
            promoType: promotionType || undefined,
            dim2Name: dim2AttributeName,
            planId,
            promotionId: effectivePromotionId,
            onMetricChange,
            mutateAsync: upliftMutateAsync,
            isPending: isUpliftPending,
            fieldStates: promoFieldStates,
        });

    const handleAddProducts = useCallback(
        (ppgIds: number[]) => {
            const { newIds } = onAddProducts(ppgIds, existingPpgIds);
            setSelectedPpgIds(newIds);
        },
        [onAddProducts, existingPpgIds],
    );

    const { isDirty, canSave } = usePromoDirtyState({
        isEditMode,
        ppgData,
        baseline,
        promoFormValues,
        existingPromoFormValues,
        existingPpgIds,
        hasProducts,
        isLoadingRecommendedValues,
        hasFormErrors,
        isUpliftPending,
        fieldStates: promoFieldStates,
    });

    // --- mutation ---
    const handleSaveSuccess = useCallback(
        (data: { promotion_id?: string; data?: { promotion_id?: string } }) => {
            const promoId = data?.promotion_id ?? data?.data?.promotion_id;
            if (promoId) {
                setSavedPromotionId(promoId);
                onPromoSaved?.(promoId);
            }
            promoForm.resetFields(noApiFieldNames);
            snapshotBaseline();
            setSelectedPpgIds([]);
        },
        [promoForm, snapshotBaseline, onPromoSaved, noApiFieldNames],
    );

    const { mutate: createPromo, isPending: isPromoMutationPending } = useCreatePromoMutation({
        onSuccess: handleSaveSuccess,
    });

    const { save, reset: resetPromo } = usePromoSave({
        promoForm,
        ppgData,
        baseline,
        planId,
        eventId,
        savedPromotionId,
        promoMeta,
        existingPromoFormValues,
        existingPpgData,
        isEditMode,
        createPromo,
        resetPpgToExisting,
        clearPpgState,
        triggerUpliftBeforeSave,
    });

    const reset = useCallback(() => {
        resetPromo();
        setSelectedPpgIds([]);
    }, [resetPromo]);

    const handleFormValuesChange = useCallback(
        (changedValues: Record<string, unknown>) => {
            const changedKeys = Object.keys(changedValues);
            if (changedKeys.includes('promoType')) {
                resetEditableSpendsToZero();
            }
            // Deterministic form -> metric cascade. Merge changed over watched values so the
            // just-changed field is fresh (Form.useWatch lags one render behind onValuesChange).
            // TODO: switching `manualOffer` does not re-run the form -> metric cascade (it is not a
            // rule trigger), so the previously seeded promoted_srp persists until the newly shown
            // offer field gets a value. Revisit if an offer-method switch should reset/recompute it.
            if (changedKeys.some((key) => cascadeTriggerFieldNames.has(key))) {
                applyFormCascade({ ...promoFormValues, ...changedValues });
            }
            // Re-fetch uplift only on user-initiated offer edits. Driving this off onValuesChange
            // (rather than watching derived form values) avoids spurious calls when the form is
            // populated programmatically on load/refetch — e.g. right after a save.
            if (changedKeys.some((key) => offerTriggerFieldNames.has(key))) {
                triggerUpliftDebounced();
            }
        },
        [
            resetEditableSpendsToZero,
            triggerUpliftDebounced,
            offerTriggerFieldNames,
            cascadeTriggerFieldNames,
            applyFormCascade,
            promoFormValues,
        ],
    );

    // --- effects ---
    useEffect(() => {
        if (existingPromoFormValues) {
            promoForm.setFieldsValue(existingPromoFormValues);
            if (ruleStore) {
                ruleStore.getState().setPromoValues(instanceId, existingPromoFormValues, { source: 'hydrate' });
            }
        }
    }, [existingPromoFormValues, promoForm, ruleStore, instanceId]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Auto-trigger uplift when new recommended values are fetched (PPG/date/tactic change).
    // `fetchedPpgData` arrives one commit before `ppgData` (derived in usePpgData via async
    // setPpgData) reflects it, so we (1) record the new fetch + the ppgData seen at that moment,
    // then (2) fire only once ppgData has actually committed the new rows. The "consumed fetch"
    // marker is advanced only when we fire, which avoids the previous state-commit race and
    // guarantees the uplift payload is built from the freshly fetched metrics.
    const lastUpliftedFetchRef = useRef<ApiPPG[] | null>(null);
    const pendingFetchRef = useRef<{ fetch: ApiPPG[]; ppgAtDetect: PPGRow[] } | null>(null);

    useEffect(() => {
        if (
            fetchedPpgData.length > 0 &&
            fetchedPpgData !== lastUpliftedFetchRef.current &&
            pendingFetchRef.current?.fetch !== fetchedPpgData
        ) {
            pendingFetchRef.current = { fetch: fetchedPpgData, ppgAtDetect: ppgData };
        }

        const pending = pendingFetchRef.current;
        if (
            pending &&
            ppgData !== pending.ppgAtDetect &&
            ppgData.length > 0 &&
            isUpliftApplicable &&
            !isUpliftPending
        ) {
            lastUpliftedFetchRef.current = pending.fetch;
            pendingFetchRef.current = null;
            triggerUpliftAuto();
        }
    }, [fetchedPpgData, ppgData, isUpliftApplicable, isUpliftPending, triggerUpliftAuto]);

    return (
        <div>
            <div className="flex flex-row justify-between items-center">
                <p className="text-base font-medium">{effectivePromotionId ? 'Edit Promo' : 'Add New Promo'}</p>
                <div className="flex flex-row gap-x-2">
                    <Button
                        type="primary"
                        autoFocus={true}
                        onClick={save}
                        disabled={!canSave}
                        loading={isPromoMutationPending}
                    >
                        {effectivePromotionId ? 'Update Promo' : 'Save Promo'}
                    </Button>
                    <Button type="default" onClick={reset}>
                        {'Reset'}
                    </Button>
                </div>
            </div>
            <Tabs
                items={[
                    {
                        key: '1',
                        label: 'Designer',
                        children: (
                            <PromoDesigner
                                promoForm={promoForm}
                                instanceId={instanceId}
                                ppgData={ppgData}
                                baseline={baseline}
                                onMetricChange={onMetricChange}
                                onFormValuesChange={handleFormValuesChange}
                                onAddProducts={handleAddProducts}
                                isAddProductsDisabled={isAddProductsDisabled}
                                isLoadingRecommendedValues={isLoadingRecommendedValues}
                                isLoadingNextPage={isLoadingNextPage}
                                hasMoreRecommendedValues={hasMoreRecommendedValues}
                                fetchNextRecommendedPage={fetchNextRecommendedPage}
                                hasProducts={hasProducts}
                                promoType={promoFormValues['promoType'] as string | undefined}
                                existingProductIds={existingPpgIds}
                                eventId={eventId}
                                isLoadingPromoData={isLoadingPromoData}
                                promotionId={effectivePromotionId}
                                parsedOptions={parsedPromoOptions}
                                productOptions={productOptions}
                                isLoadingProducts={isLoadingProducts}
                                onRefreshUplift={triggerUplift}
                                isUpliftPending={isUpliftPending}
                                isUpliftApplicable={isUpliftApplicable}
                            />
                        ),
                    },
                    {
                        key: '2',
                        label: 'Insights',
                        children: <p className="text-xl text-[#9F9FA9]">{'No Insights Available'}</p>,
                        disabled: true,
                    },
                ]}
            />
        </div>
    );
};

PromoTab.displayName = 'PromoTab';
export default PromoTab;
